import React, { useState, useEffect, useContext, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Share2, UserPlus, Trash2, Plus, ExternalLink } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { AuthContext } from '@/App';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const WishlistDetail = () => {
  const { wishlistId } = useParams();
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const [wishlist, setWishlist] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [collaboratorDialogOpen, setCollaboratorDialogOpen] = useState(false);
  const [collaboratorEmail, setCollaboratorEmail] = useState('');
  const [fetchingImage, setFetchingImage] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [formData, setFormData] = useState({
    product_name: '',
    product_url: '',
    product_image_url: '',
    price: ''
  });

  const fetchWishlistData = useCallback(async () => {
    try {
      const [wishlistRes, itemsRes] = await Promise.all([
        axios.get(`${API}/wishlists/${wishlistId}`, { withCredentials: true }),
        axios.get(`${API}/wishlists/${wishlistId}/items`, { withCredentials: true })
      ]);
      setWishlist(wishlistRes.data);
      setItems(itemsRes.data);
    } catch (error) {
      console.error('Error fetching wishlist:', error);
      toast.error('Failed to load wishlist');
    } finally {
      setLoading(false);
    }
  }, [wishlistId]);

  useEffect(() => {
    fetchWishlistData();
  }, [fetchWishlistData]);

  // Check if current user is creator or collaborator
  const isCreatorOrCollaborator = () => {
    if (!wishlist || !user) return false;
    return wishlist.creator_id === user.id || (wishlist.collaborators || []).includes(user.id);
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.size > 2 * 1024 * 1024) {
      toast.error('Image size should be less than 2MB');
      return;
    }

    setUploadingImage(true);
    const uploadFormData = new FormData();
    uploadFormData.append('file', file);

    try {
      const response = await axios.post(`${API}/upload-image`, uploadFormData, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setFormData(prev => ({ ...prev, product_image_url: response.data.url }));
      toast.success('Image uploaded!');
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Failed to upload image');
    } finally {
      setUploadingImage(false);
    }
  };

  const handleFetchProduct = async () => {
    if (!formData.product_url) return;
    
    setFetchingImage(true);
    try {
      const response = await axios.post(`${API}/fetch-product`, { url: formData.product_url });
      console.log('Fetched product data:', response.data);
      
      const newData = {
        ...formData,
        product_name: response.data.product_name || formData.product_name,
        product_image_url: response.data.product_image_url || formData.product_image_url,
        price: response.data.price || formData.price
      };
      setFormData(newData);
      
      const fetched = [];
      if (response.data.product_name) fetched.push('name');
      if (response.data.product_image_url) {
        fetched.push('image');
        console.log('Image URL fetched:', response.data.product_image_url);
      } else {
        console.warn('No image URL in response');
      }
      if (response.data.price) fetched.push('price');
      
      if (fetched.length === 0) {
        toast.warning('Could not auto-fill details. Please enter manually.');
      } else {
        toast.success(`Fetched: ${fetched.join(', ')}! Review and edit if needed.`);
      }
    } catch (error) {
      console.error('Error fetching product:', error);
      const errorMsg = error.response?.data?.detail || 'Failed to fetch details';
      toast.error(errorMsg + ' Please enter manually.');
    } finally {
      setFetchingImage(false);
    }
  };

  const handleAddItem = async (e) => {
    e.preventDefault();
    
    if (!formData.product_name) {
      toast.error('Please enter product name');
      return;
    }
    if (!formData.price) {
      toast.error('Please enter price');
      return;
    }
    if (!formData.product_url) {
      toast.error('Please enter product URL');
      return;
    }

    try {
      const imageUrl = formData.product_image_url || 
        'https://via.placeholder.com/300x300/e5e7eb/6b7280?text=No+Image';
      
      console.log('Adding item with image URL:', imageUrl);
      
      const itemData = {
        product_name: formData.product_name,
        product_image_url: imageUrl,
        product_url: formData.product_url,
        price: parseFloat(formData.price)
      };
      
      console.log('Item data being sent:', itemData);
      
      await axios.post(`${API}/wishlists/${wishlistId}/items`, itemData, { withCredentials: true });
      toast.success('Item added to wishlist!');
      setDialogOpen(false);
      setFormData({ product_name: '', product_url: '', product_image_url: '', price: '' });
      fetchWishlistData();
    } catch (error) {
      console.error('Error adding item:', error);
      toast.error('Failed to add item');
    }
  };

  const handleDeleteItem = async (itemId) => {
    if (!window.confirm('Are you sure you want to delete this item?')) return;

    try {
      await axios.delete(`${API}/wishlists/${wishlistId}/items/${itemId}`, { withCredentials: true });
      toast.success('Item deleted successfully');
      fetchWishlistData();
    } catch (error) {
      console.error('Error deleting item:', error);
      toast.error('Failed to delete item');
    }
  };

  const handleShareCollaborator = async (e) => {
    e.preventDefault();
    if (!collaboratorEmail) {
      toast.error('Please enter an email');
      return;
    }

    try {
      await axios.post(`${API}/wishlists/${wishlistId}/share`, {
        collaborator_email: collaboratorEmail
      }, { withCredentials: true });
      toast.success('Collaborator added successfully!');
      setCollaboratorDialogOpen(false);
      setCollaboratorEmail('');
      fetchWishlistData();
    } catch (error) {
      console.error('Error sharing wishlist:', error);
      toast.error(error.response?.data?.detail || 'Failed to add collaborator');
    }
  };

  const handleShareWishlist = () => {
    // Find an event using this wishlist
    const wishlistUrl = `${window.location.origin}/wishlists/${wishlistId}`;
    const message = `Check out my wishlist: ${wishlist.name}!\n\n${wishlistUrl}`;
    const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, '_blank');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (!wishlist) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <p className="text-gray-500 text-lg mb-4">Wishlist not found</p>
          <Button onClick={() => navigate('/dashboard')}>Go to Dashboard</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen p-4 sm:p-6">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="glass-card p-6 mb-6">
          <Button
            onClick={() => navigate('/dashboard')}
            variant="outline"
            className="mb-4"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Dashboard
          </Button>
          
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-800 mb-2">{wishlist.name}</h1>
              {wishlist.creator_name && (
                <p className="text-sm text-gray-500">Created by {wishlist.creator_name}</p>
              )}
            </div>
            <div className="flex flex-wrap gap-2">
              <Dialog open={collaboratorDialogOpen} onOpenChange={setCollaboratorDialogOpen}>
                <DialogTrigger asChild>
                  <Button variant="outline" className="gap-2">
                    <UserPlus className="w-4 h-4" />
                    Add Collaborator
                  </Button>
                </DialogTrigger>
                <DialogContent className="sm:max-w-md">
                  <DialogHeader>
                    <DialogTitle>Share Wishlist</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleShareCollaborator} className="space-y-4">
                    <div>
                      <Label>Collaborator Email</Label>
                      <Input
                        type="email"
                        placeholder="friend@example.com"
                        value={collaboratorEmail}
                        onChange={(e) => setCollaboratorEmail(e.target.value)}
                        required
                      />
                    </div>
                    <Button type="submit" className="w-full">Add Collaborator</Button>
                  </form>
                </DialogContent>
              </Dialog>

              <Button onClick={handleShareWishlist} className="gap-2 bg-green-500 hover:bg-green-600">
                <Share2 className="w-4 h-4" />
                Share Wishlist
              </Button>
            </div>
          </div>
        </div>

        {/* Add Item Button */}
        <div className="mb-6">
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button className="gap-2 bg-gradient-to-r from-purple-400 to-purple-500 hover:from-purple-500 hover:to-purple-600">
                <Plus className="w-4 h-4" />
                Add Item
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-md max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Add Item to Wishlist</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleAddItem} className="space-y-4">
                <div>
                  <Label htmlFor="product_url">
                    Product URL * 
                    <span className="text-xs text-gray-500 ml-2">(Paste link from Amazon, Myntra, etc.)</span>
                  </Label>
                  <div className="flex gap-2">
                    <Input
                      id="product_url"
                      placeholder="https://amazon.in/..."
                      value={formData.product_url}
                      onChange={(e) => setFormData({ ...formData, product_url: e.target.value })}
                      onPaste={(e) => {
                        e.preventDefault();
                        const pastedText = e.clipboardData.getData('text').trim();
                        setFormData({ ...formData, product_url: pastedText });
                      }}
                      required
                    />
                    <Button
                      type="button"
                      onClick={handleFetchProduct}
                      disabled={fetchingImage || !formData.product_url || formData.product_url.length < 10}
                      className="whitespace-nowrap bg-blue-500 hover:bg-blue-600"
                    >
                      {fetchingImage ? '⏳ Fetching...' : '🔍 Auto-Fill'}
                    </Button>
                  </div>
                </div>

                <div className="border-t pt-4">
                  <div className="space-y-3">
                    <div>
                      <Label htmlFor="product_name">Product Name *</Label>
                      <Input
                        id="product_name"
                        placeholder="e.g., Smart Watch"
                        value={formData.product_name}
                        onChange={(e) => setFormData({ ...formData, product_name: e.target.value })}
                        required
                      />
                    </div>
                    
                    <div>
                      <Label htmlFor="price">Price (₹) *</Label>
                      <Input
                        id="price"
                        type="number"
                        step="0.01"
                        placeholder="2999"
                        value={formData.price}
                        onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                        required
                      />
                    </div>
                    
                    <div>
                      <Label htmlFor="product_image_url">
                        Product Image 
                        <span className="text-xs text-gray-500 ml-2">(Optional)</span>
                      </Label>
                      <Input
                        id="product_image_url"
                        placeholder="https://..."
                        value={formData.product_image_url}
                        onChange={(e) => setFormData({ ...formData, product_image_url: e.target.value })}
                      />
                      
                      <div className="mt-2">
                        <Input
                          type="file"
                          accept="image/*"
                          onChange={handleImageUpload}
                          className="text-sm"
                        />
                      </div>
                      
                      {formData.product_image_url && (
                        <img 
                          src={formData.product_image_url} 
                          alt="Preview" 
                          className="mt-2 w-full h-32 object-cover rounded border"
                        />
                      )}
                    </div>
                  </div>
                </div>

                <Button type="submit" className="w-full" disabled={uploadingImage}>
                  {uploadingImage ? '⏳ Uploading Image...' : 'Add to Wishlist'}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Items Grid */}
        <div className="glass-card p-6">
          <h2 className="text-xl font-bold text-gray-800 mb-4">Items ({items.length})</h2>
          
          {items.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-gray-500 text-lg mb-2">No items in this wishlist yet</p>
              <p className="text-gray-400">Click &quot;Add Item&quot; to get started!</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {items.map((item) => (
                <div key={item.id} className="glass-card p-4 relative group">
                  <Button
                    onClick={() => handleDeleteItem(item.id)}
                    variant="ghost"
                    size="icon"
                    className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity h-8 w-8"
                  >
                    <Trash2 className="w-4 h-4 text-red-500" />
                  </Button>
                  
                  <img 
                    src={item.product_image_url} 
                    alt={item.product_name}
                    className="w-full h-48 object-cover rounded mb-3"
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = 'https://via.placeholder.com/300x300/e5e7eb/6b7280?text=No+Image';
                    }}
                  />
                  <h3 className="font-semibold text-gray-800 mb-1">{item.product_name}</h3>
                  <p className="text-lg font-bold text-purple-600 mb-2">₹{item.price}</p>
                  
                  {item.selected_by && (
                    <p className="text-sm text-green-600 font-medium">
                      {isCreatorOrCollaborator() && item.selected_by_name ? (
                        <>
                          ❤️ Selected by {item.selected_by_name}
                        </>
                      ) : (
                        <>
                          ❤️ Reserved by a loved one
                        </>
                      )}
                    </p>
                  )}
                  
                  {item.product_url && (
                    <a 
                      href={item.product_url} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-xs text-blue-500 hover:underline flex items-center gap-1 mt-2"
                    >
                      <ExternalLink className="w-3 h-3" />
                      View Product
                    </a>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default WishlistDetail;
